import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatPhoneDisplay, formatPhoneForWhatsApp } from "@/utils/phoneFormat";
import { fmtNum } from "@/utils/toEnglishDigits";
import {
  X as XIcon,
  Phone,
  WhatsappLogo,
  Envelope,
  Note,
  Pencil,
  ClipboardText,
  UserCircle,
} from "@phosphor-icons/react";

/**
 * ClientDetailsModal — read-only view of a company client's profile.
 *
 * Props:
 *   open, client, onClose, onEdit(client)
 *
 * When `open`, fetches `/company/clients/{id}` to load fresh data + stats
 * (total previous bookings). `client` provided is the fallback while loading.
 */
export default function ClientDetailsModal({ open, client, onClose, onEdit }) {
  const [detail, setDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) {
      setDetail(null);
      setStats(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/company/clients/${client.id}`);
        if (cancelled) return;
        setDetail(data?.client || client);
        setStats(data?.stats || null);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?.id]);

  if (!open) return null;

  const c = detail || client || {};
  const phones = Array.isArray(c.phones) && c.phones.length > 0
    ? c.phones
    : [c.phone1, c.phone2].filter(Boolean);
  const initial = (c.name || "؟").trim().charAt(0).toUpperCase();

  const waLink = (phone) => {
    const p = formatPhoneForWhatsApp(phone);
    if (!p) return null;
    const message = `مرحباً ${c.name || ""}،`;
    return `https://wa.me/${p}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="client-details-modal"
    >
      <div
        className="bg-card border border-primary/25 rounded-2xl w-full max-w-lg gold-glow max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-primary/15 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-primary font-bold text-base">
            <UserCircle size={18} weight="duotone" /> بيانات العميل
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/50 hover:text-white"
            data-testid="client-details-close"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Avatar + name */}
          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-black text-primary-foreground shrink-0"
              style={{
                background: "linear-gradient(135deg,#C9A961 0%,#E1C484 100%)",
                boxShadow: "0 6px 16px rgba(201,169,97,0.35)",
              }}
              aria-hidden
            >
              {initial}
            </div>
            <div className="min-w-0">
              <div className="text-white font-bold text-lg truncate" data-testid="client-details-name">
                {c.name || "—"}
              </div>
              {c.email && (
                <div className="text-white/55 text-xs mt-0.5 inline-flex items-center gap-1 truncate" dir="ltr">
                  <Envelope size={11} className="text-primary/60" /> {c.email}
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <StatBox
              icon={<ClipboardText size={16} weight="duotone" />}
              label="حجوزات سابقة"
              value={loading ? "…" : fmtNum(stats?.total_bookings || 0)}
              testId="stat-bookings"
            />
            <StatBox
              icon={<Phone size={16} weight="duotone" />}
              label="أرقام مسجّلة"
              value={fmtNum(phones.length)}
              testId="stat-phones"
            />
          </div>

          {/* Phones */}
          {phones.length > 0 && (
            <div>
              <div className="text-xs text-white/50 mb-2 text-right font-bold">أرقام التواصل</div>
              <div className="space-y-2">
                {phones.map((p, i) => {
                  const wa = waLink(p);
                  return (
                    <div
                      key={`${p}-${i}`}
                      className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-primary/15 bg-black/20"
                      data-testid={`client-details-phone-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0" dir="ltr">
                        <Phone size={13} weight="fill" className="text-primary/70 shrink-0" />
                        <span className="num text-white text-sm truncate">
                          {formatPhoneDisplay(p)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <a
                          href={`tel:+${String(p).replace(/\D/g, "")}`}
                          className="w-8 h-8 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 flex items-center justify-center"
                          title="اتصال"
                          data-testid={`client-details-tel-${i}`}
                        >
                          <Phone size={13} weight="bold" />
                        </a>
                        {wa && (
                          <a
                            href={wa}
                            target="_blank"
                            rel="noreferrer"
                            className="w-8 h-8 rounded-lg bg-[#25D366]/15 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/25 flex items-center justify-center"
                            title="واتساب"
                            data-testid={`client-details-wa-${i}`}
                          >
                            <WhatsappLogo size={13} weight="fill" />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {c.notes && (
            <div>
              <div className="text-xs text-white/50 mb-2 text-right font-bold inline-flex items-center gap-1">
                <Note size={12} weight="duotone" className="text-primary/70" /> ملاحظات
              </div>
              <div
                className="rounded-xl border border-primary/15 bg-black/20 p-3 text-white/85 text-sm whitespace-pre-wrap"
                data-testid="client-details-notes"
              >
                {c.notes}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 p-4 border-t border-primary/15 bg-[#0B1A3A]/40">
          <button
            type="button"
            onClick={() => onEdit?.(c)}
            className="flex-1 py-2.5 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-sm inline-flex items-center justify-center gap-1.5"
            data-testid="client-details-edit"
          >
            <Pencil size={13} weight="bold" /> تعديل البيانات
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-white/20 text-white/70 text-sm hover:bg-white/5"
            data-testid="client-details-close-btn"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon, label, value, testId }) {
  return (
    <div
      className="rounded-xl border border-primary/20 bg-primary/5 p-3 flex items-center gap-2"
      data-testid={testId}
    >
      <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 text-primary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-primary font-black text-lg num leading-tight" dir="ltr">{value}</div>
        <div className="text-white/55 text-[11px]">{label}</div>
      </div>
    </div>
  );
}
